import dayjs from "dayjs";

const getIsoDate = (d) =>
  d ? dayjs(d).utc().startOf("day").toISOString() : null;
const isObject = (v) => v != null && typeof v === "object" && !Array.isArray(v);

const diffDetails = (form = {}, prev = {}) => {
  const fields = ["materialId", "treeId", "partsPerTree"];
  const changed = fields.some((f) => (form[f] ?? null) !== (prev[f] ?? null));
  if (!changed) return null;
  return {
    materialId: form.materialId ?? null,
    treeId: form.treeId ?? null,
    partsPerTree: form.partsPerTree ?? null,
  };
};

const mapRoutingRow = (r, idx) => ({
  id: r?.id,
  stationId: r.stationId ?? null,
  leadTime: r.leadTime ?? 0,
  cycleTime: r.cycleTime ?? 0,
  notes: r.notes ?? "",
  config: r.config ?? {},
  order: r.order ?? idx,
  quantity: r.quantity ?? 0,
  plannedStartDate: getIsoDate(r.plannedStartDate),
  plannedEndDate: getIsoDate(r.plannedEndDate),
});

const mapInstructions = (ins) => ({
  id: ins?.id,
  departmentId: ins?.departmentId,
  notes: ins?.notes ?? "",
  config: ins?.config ?? {},
  routing: ins?.routing?.map(mapRoutingRow) || [],
});

const mapRoutes = (ins) => ins.routing?.map((r) => r?.id && Number(r?.id));
const mapInstruction = (ins) => ins?.id && Number(ins?.id);
const collectDeleted = (
  { curr: formInstructions = [], prev: prevInstructions = [] },
  mapper,
) => {
  const curr = formInstructions.map(mapper).flat().filter(Boolean);
  const prev = prevInstructions.map(mapper).flat().filter(Boolean);
  const deleted = prev?.filter((id) => id && !curr.includes(id));
  return deleted;
};

const getDifference = (curr, prev) => {
  if (!isObject(curr) || !isObject(prev)) return;
  const diff = Object.entries(curr).reduce((acc, [key, value]) => {
    if (isObject(prev[key]) || isObject(value)) {
      const diffedKey = getDifference(value, prev[key]);
      const isDif = Object.keys(diffedKey).length;
      if (isDif && key === "config")
        acc[key] = value; // If not entirely set, DB will replace entire column
      else if (isDif) acc[key] = diffedKey; // If not config then don't worry about it
    } else if (prev[key] !== value) acc[key] = value;
    return acc;
  }, {});
  if (!Object.keys(diff)?.length) return {};
  return diff;
};

const getDiffRouting = (curr, prev) => {
  const upserted = curr
    .map((route) => {
      const existing = prev.find((r) => r.id === route.id);
      const id = existing?.id;
      if (id) {
        const updated = getDifference(route, existing);
        const isUpdated = !!Object.keys(updated).length;
        // config is sent whole or not at all; omitting it lets the API's
        // Joi `.default({})` overwrite the stored recipe with an empty object.
        return isUpdated ? { id, config: route.config, ...updated } : false;
      } else return route;
    })
    .filter(Boolean);

  return upserted;
};

// Per-section diff: notes, config, AND nested routing rows. Returns null
// when nothing in the section changed.
const diffInstructionSection = ({ curr = [], prev = [] }) => {
  const deletedInstructions = collectDeleted({ curr, prev }, mapInstruction);
  const deletedRouting = collectDeleted({ curr, prev }, mapRoutes);

  const instructions = curr
    ?.map((ins) => {
      const existing = prev.find((p) => p.id === ins.id);
      const id = existing?.id;
      if (id) {
        const { routing: currRouting, ...currIns } = ins;
        const { routing: prevRouting, ...prevIns } = existing;

        const details = getDifference(currIns, prevIns);
        const routing = getDiffRouting(currRouting, prevRouting);
        const updated = {
          ...details,
          ...(routing?.length ? { routing } : {}),
        };
        const isUpdated = !!Object.keys(updated).length;
        return isUpdated ? { id, ...updated } : false;
      }
      // does not exist in previous therefore whole instructions can be added
      else return ins;
    })
    .filter(Boolean);

  return { deletedInstructions, deletedRouting, instructions };
};

export const updateWorkOrderInstructionsMapper = (
  formData,
  defaultValues,
  id,
) => {
  // Top-level scalars
  const payload = {
    id,
    ...(formData.status !== defaultValues.status && {
      status: formData?.status,
    }),
    ...(formData.priority !== defaultValues.priority && {
      priority: formData?.priority,
    }),
    ...(formData.routeId !== defaultValues.routeId && {
      routeId: formData?.routeId,
    }),
  };

  // WO-level promoted columns (material / tree / partsPerTree)
  const details = diffDetails(formData.details, defaultValues.details);
  if (details) payload.details = details;

  // Instruction & Routing Changes Diffs
  const { deletedInstructions, deletedRouting, instructions } =
    diffInstructionSection({
      curr: formData.instructions?.map(mapInstructions),
      prev: defaultValues.instructions?.map(mapInstructions),
    });

  if (instructions.length) payload.instructions = instructions;
  if (deletedRouting.length) payload.deletedRoutes = deletedRouting;
  if (deletedInstructions.length)
    payload.deletedInstructions = deletedInstructions;

  // Labor edits
  if (formData.updatedLabor?.length) {
    payload.updatedLabor = formData.updatedLabor.map((l) => ({
      id: Number(l.laborId),
      userId: Number(l.userId),
      completed: Number(l.completed),
      scrapped: Number(l.scrapped),
      startDate: getIsoDate(l.startDate),
      endDate: getIsoDate(l.endDate),
      notes: l?.notes,
    }));
  }

  return payload;
};
